import { SyncedInput } from "@/components/SyncedInput";
import React, { useRef, useState } from "react";
import { toast } from "sonner";
import ReflexKoppelingPanel from "@/components/tabs/ReflexKoppelingPanel";

export default function GereedTab({ xl }) {
  const { Download, Trash2, Card, Kpi, Table, euro, kgf, toNum, user, roleLabel, canEdit, btn, exportCSV,
    gereedVoorraad, gereedAggregated, gereedVoorraadWaarde, uploadGereed, reflexSync, isAdmin, api, setGereedKostprijs, adoptGereedKostprijs, gereedStamRef, adoptGereedStam, clearGereedLocatie, removeGereedArtikel,
    gereedHistorie, saveGereedSnapshot, removeGereedSnapshot } = xl;
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [snapMaand, setSnapMaand] = useState(() => new Date().toISOString().slice(0, 7));
  const [openSnap, setOpenSnap] = useState(null);

  const agg = gereedAggregated();
  const locaties = [...new Set(gereedVoorraad.map((r) => r.locatie))];
  const lastUpload = (loc) => {
    const rows = gereedVoorraad.filter((r) => r.locatie === loc && r.uploadedAt);
    if (!rows.length) return null;
    return rows.map((r) => r.uploadedAt).sort().slice(-1)[0];
  };
  const totAantal = agg.reduce((a, x) => a + x.aantal, 0);
  const totKg = agg.reduce((a, x) => a + x.gewicht, 0);
  const zonderKostprijs = agg.filter((x) => x.kostprijs <= 0).length;

  const onFile = async (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    setBusy(true);
    await uploadGereed(f);
    setBusy(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const enteredPerKg = (x, val) => {
    const v = toNum(val);
    if (v <= 0) return null;
    if (x.basis === "stuk") { const kps = x.aantal > 0 ? x.gewicht / x.aantal : 0; return kps > 0 ? v / kps : null; }
    return v;
  };
  const devInfo = (x) => {
    if (!x.berekend || !(x.berekend.prijs > 0)) return null;
    const pk = enteredPerKg(x, x.kostprijs);
    if (pk == null) return null;
    const pct = (pk - x.berekend.prijs) / x.berekend.prijs;
    if (Math.abs(pct) < 0.02) return { txt: "≈ gelijk", cls: "text-emerald-600" };
    return pct > 0
      ? { txt: `▲ ${(pct * 100).toFixed(0)}% boven`, cls: "text-amber-600" }
      : { txt: `▼ ${(Math.abs(pct) * 100).toFixed(0)}% onder`, cls: "text-rose-600" };
  };
  const warnDeviation = (x, val) => {
    if (!x.berekend || !(x.berekend.prijs > 0)) return;
    const pk = enteredPerKg(x, val);
    if (pk == null) return;
    const pct = (pk - x.berekend.prijs) / x.berekend.prijs;
    if (Math.abs(pct) < 0.02) return;
    const richting = pct > 0 ? "boven" : "onder";
    const perStuk = x.basis === "stuk" && x.aantal > 0 ? ` (≈ ${euro(x.berekend.prijs * (x.gewicht / x.aantal))}/stuk)` : "";
    toast.warning(`Let op: kostprijs voor ${x.artikelnr} ${x.naam} ligt ${Math.abs(pct * 100).toFixed(0)}% ${richting} de gemiddelde productie-kostprijs van ${euro(x.berekend.prijs)}/kg${perStuk}.`);
  };

  return (
    <div className="space-y-6" data-testid="gereed-tab">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 mb-1">Voorraad gereed product</h1>
          <p className="text-sm text-slate-500 max-w-3xl">Voorraad gereed product uit ReflexBlue (vrieshuis Lelieveld + Sprundel). Upload de Reflex-export; per artikel vul je zelf de kostprijs in. De voorraadwaarde telt automatisch mee in de maandafsluiting.</p>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <SyncedInput ref={fileRef} type="file" accept=".xlsx,.xls,.csv" data-testid="gereed-upload-input" onChange={onFile} className="hidden" />
            {isAdmin && (
              <button data-testid="gereed-live-sync" disabled={syncing} onClick={async () => { setSyncing(true); try { await reflexSync(); } finally { setSyncing(false); } }} className={btn}>
                <Download className="h-4 w-4 mr-2 inline" />{syncing ? "Live ophalen…" : "Live verversen (ReflexBlue)"}
              </button>
            )}
            <button data-testid="gereed-upload-btn" disabled={busy} onClick={() => fileRef.current && fileRef.current.click()} className={isAdmin ? "rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50" : btn}>
              <Download className="h-4 w-4 mr-2 inline" />{busy ? "Bezig met inlezen…" : "Reflex-export uploaden"}
            </button>
            {agg.length > 0 && (
              <button data-testid="gereed-export" onClick={() => exportCSV("voorraad-gereed-product.csv", ["Artikelnr", "Naam", "Aantal", "Gewicht kg", "Kostprijs", "Basis", "Voorraadwaarde"], agg.map((x) => [x.artikelnr, x.naam, x.aantal.toFixed(0), x.gewicht.toFixed(1), x.kostprijs.toFixed(2), x.basis === "kg" ? "per kg" : "per stuk", x.waarde.toFixed(2)]))} className={btn}>
                <Download className="h-4 w-4 mr-2 inline" />Export CSV
              </button>
            )}
          </div>
        )}
      </div>

      {!canEdit && <div data-testid="readonly-banner" className="rounded-md border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800">Alleen-lezen: je rol ({roleLabel(user?.role)}) mag deze voorraad bekijken maar niet wijzigen.</div>}

      {isAdmin && <ReflexKoppelingPanel api={api} btn={btn} />}

      {locaties.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="gereed-locaties">
          {locaties.map((loc) => (
            <div key={loc} className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-600">
              <span className="font-semibold text-slate-800">{loc}</span>
              <span className="text-slate-400">· {gereedVoorraad.filter((r) => r.locatie === loc).length} regels</span>
              {lastUpload(loc) && <span className="text-slate-400">· {new Date(lastUpload(loc)).toLocaleDateString("nl-NL")}</span>}
              {canEdit && <button data-testid={`gereed-clear-${loc}`} onClick={() => clearGereedLocatie(loc)} title="Voorraad van deze locatie wissen" className="text-slate-400 hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button>}
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi label="Artikelen" value={agg.length} />
        <Kpi label="Totaal stuks" value={new Intl.NumberFormat("nl-NL").format(Math.round(totAantal))} />
        <Kpi label="Totaal gewicht" value={kgf(totKg)} />
        <Kpi label="Totale voorraadwaarde" value={euro(gereedVoorraadWaarde)} />
      </div>

      {zonderKostprijs > 0 && (
        <div data-testid="gereed-missing-banner" className="rounded-md border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-700">
          {zonderKostprijs} artikel(en) hebben nog geen kostprijs — die tellen voor €0,00 mee. Vul de kostprijs in de tabel hieronder in.
        </div>
      )}

      {agg.length === 0 ? (
        <Card><div className="py-10 text-center text-slate-400 text-sm">Nog geen voorraad ingelezen. Upload een ReflexBlue-export om te starten.</div></Card>
      ) : (
        <Table testid="gereed-row"
          columns={["Artikelnr", "Naam", "Locatie(s)", "Stuks", "Gewicht", "Kostprijs", "Basis", "Gem. kostprijs (productie)", "Stamgegevens", "Voorraadwaarde", ""]}
          rows={agg.map((x) => {
            const dev = devInfo(x);
            const sr = gereedStamRef(x);
            return [
            <span key="a" className="font-mono text-xs text-slate-500">{x.artikelnr}</span>,
            x.naam || "—",
            <span key="l" className="text-xs text-slate-500">{x.locaties.join(", ")}</span>,
            canEdit ? (
              <div key="aa" className="flex items-center gap-1">
                <SyncedInput key={`aa-${x.aantal}`} type="number" step="1" data-testid="gereed-aantal"
                  className={`w-20 rounded border px-2 py-1 text-sm ${x.aantalManual ? "border-[#1C64B0] bg-blue-50" : "border-slate-300"}`}
                  defaultValue={Math.round(x.aantal)}
                  onBlur={(e) => setGereedKostprijs(x.artikelnr, "aantalManual", e.target.value)} />
                {x.aantalManual && <button data-testid="gereed-aantal-reset" title="Terug naar import" onClick={() => setGereedKostprijs(x.artikelnr, "aantalManual", "")} className="text-slate-400 hover:text-[#1C64B0]">↺</button>}
              </div>
            ) : new Intl.NumberFormat("nl-NL").format(Math.round(x.aantal)),
            canEdit ? (
              <div key="gw" className="flex items-center gap-1">
                <SyncedInput key={`gw-${x.gewicht}`} type="number" step="0.1" data-testid="gereed-gewicht"
                  className={`w-24 rounded border px-2 py-1 text-sm ${x.gewichtManual ? "border-[#1C64B0] bg-blue-50" : (x.gewicht < 0 ? "border-rose-300 bg-rose-50" : "border-slate-300")}`}
                  defaultValue={x.gewicht}
                  onBlur={(e) => setGereedKostprijs(x.artikelnr, "gewichtManual", e.target.value)} />
                <span className="text-xs text-slate-400">kg</span>
                {x.gewichtManual && <button data-testid="gereed-gewicht-reset" title="Terug naar import" onClick={() => setGereedKostprijs(x.artikelnr, "gewichtManual", "")} className="text-slate-400 hover:text-[#1C64B0]">↺</button>}
              </div>
            ) : kgf(x.gewicht),
            canEdit ? (
              <SyncedInput key={`kp-${x.kostprijs}`} type="number" step="0.01" min="0" data-testid="gereed-kostprijs"
                className={`w-24 rounded border px-2 py-1 text-sm ${x.kostprijs <= 0 ? "border-rose-300 bg-rose-50" : "border-slate-300"}`}
                defaultValue={x.kostprijs || ""} placeholder="0,00"
                onBlur={(e) => { setGereedKostprijs(x.artikelnr, "kostprijs", e.target.value); warnDeviation(x, e.target.value); }} />
            ) : euro(x.kostprijs),
            canEdit ? (
              <select key="b" data-testid="gereed-basis" value={x.basis}
                onChange={(e) => setGereedKostprijs(x.artikelnr, "basis", e.target.value)}
                className="rounded border border-slate-300 px-2 py-1 text-sm">
                <option value="kg">per kg</option>
                <option value="stuk">per stuk</option>
              </select>
            ) : (x.basis === "kg" ? "per kg" : "per stuk"),
            x.berekend ? (
              <span key="ber" className="text-xs inline-flex items-center gap-2" data-testid="gereed-berekend">
                <span>
                  <span className="font-semibold text-slate-700">{euro(x.berekend.prijs)}/kg</span>
                  <span className="text-slate-400"> · gem. {x.berekend.n} prod.</span>
                  {dev && <span className={`ml-1 font-semibold ${dev.cls}`}>{dev.txt}</span>}
                </span>
                {canEdit && <button data-testid="gereed-adopt" onClick={() => adoptGereedKostprijs(x.artikelnr, x.berekend.prijs)} className="text-[#1C64B0] hover:underline font-semibold whitespace-nowrap">Overnemen</button>}
              </span>
            ) : <span key="ber" className="text-slate-300 text-xs">—</span>,
            sr ? (
              <span key="sr" className="text-xs inline-flex items-center gap-2" data-testid="gereed-stamref">
                <span>
                  <span className="font-semibold text-slate-700">{sr.prijs > 0 ? euro(sr.prijs) + (sr.basis === "stuk" ? "/st" : "/kg") : "—"}</span>
                  <span className="text-slate-400"> · {sr.sub}</span>
                </span>
                {canEdit && sr.prijs > 0 && <button data-testid="gereed-stam-adopt" onClick={() => adoptGereedStam(x.artikelnr, sr.prijs, sr.basis)} className="text-[#1C64B0] hover:underline font-semibold whitespace-nowrap">Overnemen</button>}
              </span>
            ) : <span key="sr" className="text-slate-300 text-xs">niet in stam</span>,
            <span key="w" className="font-semibold text-slate-800">{euro(x.waarde)}</span>,
            canEdit ? <button key="del" data-testid="gereed-del-row" title="Artikel uit voorraad verwijderen" onClick={() => removeGereedArtikel(x.artikelnr)} className="text-rose-600 hover:text-rose-800"><Trash2 className="h-4 w-4" /></button> : "",
            ];
          })} />
      )}

      {canEdit && agg.length > 0 && (
        <Card data-testid="gereed-snapshot-card">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">Maand-snapshot bewaren</div>
          <p className="text-xs text-slate-400 mb-3">Leg de huidige gereed-voorraad vast onder een maand, zodat je later kunt terugkijken wat er die maand op voorraad stond (met de toen ingevulde kostprijzen).</p>
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex flex-col text-xs font-semibold text-slate-500">Maand
              <SyncedInput type="month" data-testid="gereed-snap-maand" value={snapMaand} onChange={(e) => setSnapMaand(e.target.value)} className="mt-1 rounded-md border border-slate-300 px-3 py-2 text-sm" />
            </label>
            <button data-testid="gereed-snap-save" onClick={() => saveGereedSnapshot(snapMaand)} className={btn}>Snapshot bewaren</button>
          </div>
        </Card>
      )}

      {(gereedHistorie || []).length > 0 && (
        <details className="rounded-lg border border-slate-200 bg-slate-50/50 px-4 py-3" data-testid="gereed-historie-collapse" open>
          <summary className="cursor-pointer select-none text-lg font-medium text-slate-800 hover:text-[#1C64B0]">Maand-historie <span className="text-sm font-normal text-slate-400">({gereedHistorie.length})</span></summary>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-50 border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                <th className="px-3 py-2">Maand</th><th className="px-3 py-2">Bewaard op</th><th className="px-3 py-2">Door</th><th className="px-3 py-2 text-right">Artikelen</th><th className="px-3 py-2 text-right">Totale waarde</th><th className="px-3 py-2"></th>
              </tr></thead>
              <tbody>{gereedHistorie.slice().sort((a, b) => (a.maand < b.maand ? 1 : -1)).map((s) => (
                <tr key={s.id} className="border-t border-slate-100" data-testid="gereed-snap-row">
                  <td className="px-3 py-1.5 font-semibold">{s.maand}</td>
                  <td className="px-3 py-1.5 text-slate-500">{s.datum ? new Date(s.datum).toLocaleDateString("nl-NL") : "—"}</td>
                  <td className="px-3 py-1.5 text-slate-500">{s.door || "—"}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{s.aantal}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums font-semibold text-[#1C64B0]">{euro(s.totaal)}</td>
                  <td className="px-3 py-1.5 text-right whitespace-nowrap">
                    <button data-testid="gereed-snap-view" onClick={() => setOpenSnap(openSnap === s.id ? null : s.id)} className="text-xs font-semibold text-[#1C64B0] hover:underline mr-3">{openSnap === s.id ? "Verbergen" : "Bekijken"}</button>
                    <button data-testid="gereed-snap-csv" onClick={() => exportCSV(`gereed-voorraad-${s.maand}.csv`, ["Artikelnr", "Naam", "Locatie(s)", "Stuks", "Gewicht kg", "Kostprijs", "Basis", "Voorraadwaarde"], (s.rows || []).map((r) => [r.artikelnr, r.naam, (r.locaties || []).join(" / "), Math.round(toNum(r.aantal)), toNum(r.gewicht).toFixed(1), toNum(r.kostprijs).toFixed(2), r.basis === "kg" ? "per kg" : "per stuk", toNum(r.waarde).toFixed(2)]))} className="text-xs font-semibold text-slate-500 hover:underline mr-3">CSV</button>
                    {canEdit && <button data-testid="gereed-snap-del" onClick={() => removeGereedSnapshot(s.id)} className="text-rose-600 hover:text-rose-800"><Trash2 className="h-4 w-4 inline" /></button>}
                  </td>
                </tr>
              ))}</tbody>
            </table>
          </div>
          {openSnap && (() => {
            const s = gereedHistorie.find((x) => x.id === openSnap);
            if (!s) return null;
            return (
              <div className="mt-4" data-testid="gereed-snap-detail">
                <div className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">Detail snapshot {s.maand} ({(s.rows || []).length} artikelen)</div>
                <Table testid="gereed-snap-detail-row"
                  columns={["Artikelnr", "Naam", "Locatie(s)", "Stuks", "Gewicht", "Kostprijs", "Basis", "Voorraadwaarde"]}
                  rows={(s.rows || []).map((r) => [
                    <span key="a" className="font-mono text-xs text-slate-500">{r.artikelnr}</span>,
                    r.naam || "—",
                    <span key="l" className="text-xs text-slate-500">{(r.locaties || []).join(", ")}</span>,
                    new Intl.NumberFormat("nl-NL").format(Math.round(toNum(r.aantal))),
                    kgf(r.gewicht),
                    euro(r.kostprijs),
                    r.basis === "kg" ? "per kg" : "per stuk",
                    <span key="w" className="font-semibold text-slate-800">{euro(r.waarde)}</span>,
                  ])} />
              </div>
            );
          })()}
        </details>
      )}
    </div>
  );
}
